import React, { useState } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Divider,
  Select,
  Button,
  Input,
  message,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  updateApplicationRequest,
  fetchApplicationRequests,
} from "../../../../Store/Slices/Admin/ApplicationRequests/applicationRequestsThunk";
import { useTranslation } from "react-i18next";
import moment from "moment";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Utility component for consistent field layout
const DetailField = ({ label, value }) => (
  <Row gutter={[16, 8]} className="mb-3">
    <Col span={8}>
      <Text strong className="text-gray-600">
        {label}
      </Text>
    </Col>
    <Col span={16}>
      <Text className="text-gray-800">{value || "N/A"}</Text>
    </Col>
  </Row>
);

const ApplicationDetails = ({ testData, onClose }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("admApplications");
  const { userDetails } = useSelector((store) => store.common.user);
  const {
    statusFilter,
    applicantType,
    classId,
    sectionId,
    page,
    limit,
    searchText,
  } = useSelector((state) => state.admin.applicationRequests);

  const [status, setStatus] = useState(testData?.applicationStatus || "pending");
  const [internalComment, setInternalComment] = useState("");
  const [loading, setLoading] = useState(false);

  if (!testData)
    return <div className="p-6 text-center text-gray-500">{t("No Data Found")}</div>;

  const {
    applicant,
    applicationType,
    subject,
    description,
    fromDate,
    toDate,
    createdAt,
    className,
    sectionName,
    isLeaveApplication,
  } = testData;

  const applicantName = `${applicant?.firstName || ""} ${
    applicant?.lastName || ""
  }`.trim();
  const appliedDate = createdAt ? moment(createdAt).format("MMMM DD, YYYY") : "-";

  const refreshApplications = () => {
    dispatch(
      fetchApplicationRequests({
        status: statusFilter,
        applicantType,
        classId,
        sectionId,
        page,
        limit,
        search: searchText,
      })
    );
  };

  const handleUpdate = async () => {
    if (!internalComment.trim()) {
      message.warning(t("Please add a note before updating the application."));
      return;
    }

    setLoading(true);
    try {
      const res = await dispatch(
        updateApplicationRequest({
          applicationId: testData._id,
          status,
          comment: internalComment,
          staffId: userDetails?.userId,
        })
      );

      if (res?.payload?.success) {
        message.success(res?.payload?.message || t("Application updated successfully."));
        onClose?.();
        refreshApplications();
      } else {
        message.error(
          res?.payload?.response?.data?.message || t("Failed to update application.")
        );
      }
    } catch (error) {
      message.error(t("Something went wrong while updating."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      <Row gutter={[24, 24]}>
        {/* ======================= LEFT COLUMN ======================= */}
        <Col xs={24} lg={14}>
          <Card
            className="shadow-sm rounded-lg"
            bordered={false}
            title={
              <div className="p-2">
                <Title level={4} className="mb-0 text-gray-800">
                  {t("Application Details")}
                </Title>
                <Text type="secondary" className="text-sm">
                  {t("Submitted by")} {applicantName} {t("on")} {appliedDate}
                </Text>
              </div>
            }
            bodyStyle={{ padding: "16px 24px" }}
          >
            {/* --- Application Information --- */}
            <Title level={5} className="mt-0 mb-3 text-lg text-gray-800">
              {t("Application Information")}
            </Title>
            <DetailField label={t("Application Type")} value={applicationType} />
            <DetailField label={t("Subject")} value={subject} />

            {isLeaveApplication && (
              <DetailField
                label={t("Leave Duration")}
                value={
                  fromDate && toDate
                    ? `${moment(fromDate).format("MMM DD, YYYY")} - ${moment(
                        toDate
                      ).format("MMM DD, YYYY")}`
                    : fromDate
                    ? `${t("For Date")}: ${moment(fromDate).format("MMM DD, YYYY")}`
                    : toDate
                    ? `${t("For Date")}: ${moment(toDate).format("MMM DD, YYYY")}`
                    : "-"
                }
              />
            )}
            <DetailField label={t("Applied Date")} value={appliedDate} />

            <Row gutter={[16, 8]} className="mb-6">
              <Col span={8}>
                <Text strong className="text-gray-600">
                  {t("Description")}
                </Text>
              </Col>
              <Col span={16}>
                <div
                  className="prose max-w-none text-gray-800 text-sm"
                  dangerouslySetInnerHTML={{ __html: description || "-" }}
                ></div>
              </Col>
            </Row>

            <Divider className="my-6" />

            {/* --- Applicant Information --- */}
            <Title level={5} className="mb-3 text-lg text-gray-800">
              {t("Applicant Information")}
            </Title>
            <DetailField label={t("Name")} value={applicantName} />
            <DetailField label={t("Class")} value={className || "-"} />
            <DetailField label={t("Section")} value={sectionName || "-"} />
            <DetailField label={t("Applicant Type")} value={testData.applicantType} />
          </Card>
        </Col>

        {/* ======================= RIGHT COLUMN ======================= */}
        <Col xs={24} lg={10}>
          <Card className="shadow-sm rounded-lg" bordered={false}>
            <Title level={4} className="mt-0 mb-4 text-gray-800">
              {t("Manage Application")}
            </Title>

            {/* --- Change Status --- */}
            <div className="mb-4">
              <Text strong className="text-gray-600 block mb-1">
                {t("Change Status")}
              </Text>
              <Select
                value={status}
                onChange={setStatus}
                placeholder={t("Select Status")}
                style={{ width: "100%" }}
              >
                <Option value="pending">{t("In Progress")}</Option>
                <Option value="approved">{t("Resolved")}</Option>
                <Option value="rejected">{t("Closed")}</Option>
              </Select>
            </div>

            {/* --- Notes (Required) --- */}
            <div className="mb-4">
              <Text strong className="text-gray-600 block mb-1">
                {t("Notes")}
              </Text>
              <TextArea
                required
                rows={3}
                placeholder={t("Add internal comment (required)")}
                value={internalComment}
                onChange={(e) => setInternalComment(e.target.value)}
                className="resize-none"
              />
            </div>

            {/* --- Update Button Only --- */}
            <Button
              type="default"
              icon={<EditOutlined />}
              onClick={handleUpdate}
              block
              loading={loading}
              style={{
                color: "#ff8c00",
                borderColor: "#ff8c00",
                fontWeight: "600",
              }}
            >
              {t("Update Application")}
            </Button>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ApplicationDetails;
